import { createReducer } from '@reduxjs/toolkit';
import { IContactPreferenceSlice } from './models';
import {
  actionContactPreferenceUserFetch,
  actionContactPreferenceUserFetchSuccess,
  actionContactPreferenceUserFetchError,
  actionContactPreferenceUserSave,
  actionContactPreferenceUserSaveSuccess,
  actionContactPreferenceUserSaveError,
  actionContactPreferenceAdminFetch,
  actionContactPreferenceAdminFetchSuccess,
  actionContactPreferenceAdminFetchError,
  actionContactPreferenceAdminSave,
  actionContactPreferenceAdminSaveSuccess,
  actionContactPreferenceAdminSaveError,
} from './actions';
import { emailValidationRx, phoneSubscriberValidationRx } from './constants';
import { parsePhone, normalizePhone } from './utils';
import { getTelemetry } from '../Telemetry/Telemetry';

export const getInitialState = (): IContactPreferenceSlice => ({
  userState: null,
  userLoadTime: 0,
  userProductsOptIn: false,
  adminState: null,
  adminLoadTime: 0,
  adminOffersOptIn: false,
  adminPartnersOptIn: false,
  adminPartnersCountryCode: '1',
  adminPartnersPhoneNumber: '',
  adminPartnersPhoneNumberError: null,
  adminPartnersEmail: '',
  adminPartnersEmailError: null,
});

const telemetry = getTelemetry();
export default createReducer(getInitialState(), (builder) =>
  builder
    // User Fetch
    .addCase(actionContactPreferenceUserFetch, (state) => {
      state.userState = 'loading';
    })
    .addCase(actionContactPreferenceUserFetchSuccess, (state, { payload }) => {
      if (state.userState !== 'loading') {
        return;
      }

      if (!payload?.enabled) {
        state.userState = 'disabled';
        return;
      }

      state.userState = 'loaded';
      state.userLoadTime = Date.now();
      state.userProductsOptIn = payload.productsOptIn;
    })
    .addCase(actionContactPreferenceUserFetchError, (state) => {
      if (state.userState !== 'loading') {
        return;
      }

      state.userState = 'loadError';
    })

    // User Save
    .addCase(actionContactPreferenceUserSave, (state, { payload: { productsOptIn } }) => {
      state.userState = 'saving';
      state.userProductsOptIn = productsOptIn;
    })
    .addCase(actionContactPreferenceUserSaveSuccess, (state) => {
      if (state.userState !== 'saving') {
        return;
      }

      state.userState = 'saved';
    })
    .addCase(actionContactPreferenceUserSaveError, (state, { type, error }) => {
      if (state.userState !== 'saving') {
        return;
      }

      state.userState = 'saveError';
      telemetry.error(type, { error: error?.stack ?? error });
    })

    // Admin Fetch
    .addCase(actionContactPreferenceAdminFetch, (state) => {
      state.adminState = 'loading';
      state.adminPartnersPhoneNumberError = null;
      state.adminPartnersEmailError = null;
    })
    .addCase(actionContactPreferenceAdminFetchSuccess, (state, { payload }) => {
      if (state.adminState !== 'loading') {
        return;
      }

      state.adminState = 'loaded';
      state.adminLoadTime = Date.now();

      if (!payload) {
        return;
      }

      const { offersOptIn, partnersOptIn, partnersEmail, partnersPhoneNumber } = payload;
      const [countryCode, phoneNumber] = partnersPhoneNumber ? parsePhone(partnersPhoneNumber) : ['1', ''];

      state.adminOffersOptIn = offersOptIn;
      state.adminPartnersOptIn = partnersOptIn;
      state.adminPartnersEmail = partnersEmail;
      state.adminPartnersCountryCode = countryCode;
      state.adminPartnersPhoneNumber = phoneNumber;
    })
    .addCase(actionContactPreferenceAdminFetchError, (state) => {
      if (state.adminState !== 'loading') {
        return;
      }

      state.adminState = 'loadError';
    })

    // Admin Save
    .addCase(
      actionContactPreferenceAdminSave,
      (state, { payload: { offersOptIn, partnersOptIn, partnersEmail, partnersCountryCode, partnersPhoneNumber } }) => {
        state.adminState = 'saving';
        state.adminOffersOptIn = offersOptIn;
        state.adminPartnersOptIn = partnersOptIn;
        state.adminPartnersEmail = partnersEmail;
        state.adminPartnersCountryCode = partnersCountryCode;
        state.adminPartnersPhoneNumber = partnersPhoneNumber;

        // Validate the email address.
        const normalAdminPartnerEmail = state.adminPartnersEmail.trim();
        if (normalAdminPartnerEmail && !emailValidationRx.test(normalAdminPartnerEmail)) {
          state.adminState = 'saveError';
          state.adminPartnersEmailError = 'emailAddressInvalid';
        } else {
          state.adminPartnersEmailError = null;
        }

        // Validate the phone number.
        const normalAdminPartnersPhoneNumber = normalizePhone(state.adminPartnersPhoneNumber);
        if (normalAdminPartnersPhoneNumber && !phoneSubscriberValidationRx.test(normalAdminPartnersPhoneNumber)) {
          state.adminState = 'saveError';
          state.adminPartnersPhoneNumberError = 'phoneNumberInvalid';
        } else {
          state.adminPartnersPhoneNumberError = null;
        }
      },
    )
    .addCase(actionContactPreferenceAdminSaveSuccess, (state) => {
      if (state.adminState !== 'saving') {
        return;
      }

      state.adminState = 'saved';
    })
    .addCase(
      actionContactPreferenceAdminSaveError,
      (state, { type, payload: { partnersPhoneNumberError, partnersEmailError }, error }) => {
        if (state.adminState !== 'saving' && state.adminState !== 'saveError') {
          return;
        }

        state.adminState = 'saveError';

        if (partnersPhoneNumberError !== undefined) state.adminPartnersPhoneNumberError = partnersPhoneNumberError;
        if (partnersEmailError !== undefined) state.adminPartnersEmailError = partnersEmailError;
        if (error != null) {
          telemetry.error(type, { error: error?.stack ?? error });
        }
      },
    ),
);
